import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

class CartUpsellComponent extends Component {
  #page = 0;
  #generation = 0;
  #cache = new Map();
  #adding = false;
  #observer = new MutationObserver((records) => {
    const list = this.querySelector('[data-upsell-list]');
    if (records.some((record) =>
      (record.type === 'attributes' && record.target === this) ||
      (record.type === 'childList' && (record.target === list ||
        [...record.addedNodes].some((node) => node instanceof Element && node.matches('[data-upsell-list]'))))
    )) this.#refresh();
  });

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#observe();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#observer.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  #observe() {
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-cart-products', 'data-product-id', 'data-source', 'data-per-page'],
      childList: true,
      subtree: true,
    });
  }

  async #refresh() {
    const generation = ++this.#generation;
    this.#paginate();
    if (this.dataset.source === 'manual' || !this.dataset.productId) return;

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('section_id', 'cart-upsell-recommendations');
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'related');
    const key = `${url.href}:${this.dataset.cartProducts}`;

    try {
      if (!this.#cache.has(key)) {
        const request = fetch(url, { headers: { Accept: 'text/html' } }).then((response) => {
          if (!response.ok) throw new Error('Recommendations unavailable');
          return response.text();
        });
        if (this.#cache.size >= 12) this.#cache.clear();
        this.#cache.set(key, request);
      }
      const html = await this.#cache.get(key);
      if (!this.isConnected || generation !== this.#generation) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = parsed.querySelectorAll('[data-cart-upsell-response] [data-upsell-card]');
      const list = this.querySelector('[data-upsell-list]');
      if (cards.length && list) {
        this.#observer.disconnect();
        list.replaceChildren(...cards);
        this.#observe();
      }
      this.#paginate();
    } catch {
      this.#cache.delete(key);
    }
  }

  #paginate() {
    const cards = [...this.querySelectorAll('[data-upsell-card]')];
    const perPage = Math.max(1, Number(this.dataset.perPage) || 3);
    const pages = Math.ceil(cards.length / perPage);
    this.#page = Math.max(0, Math.min(this.#page, pages - 1));
    cards.forEach((card, index) => {
      card.hidden = Math.floor(index / perPage) !== this.#page;
    });
    this.hidden = cards.length === 0;
    const navigation = this.querySelector('[data-upsell-navigation]');
    if (navigation) navigation.hidden = pages <= 1;
    const previous = this.querySelector('[data-upsell-previous]');
    const next = this.querySelector('[data-upsell-next]');
    if (previous) previous.disabled = this.#page === 0;
    if (next) next.disabled = this.#page >= pages - 1;
    const indicator = this.querySelector('[data-upsell-page]');
    if (indicator) indicator.textContent = `${this.#page + 1} / ${pages}`;
  }

  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-upsell-variant]')) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-upsell-card]');
    if (!option || !card) return;
    const price = card.querySelector('[data-upsell-price]');
    const compare = card.querySelector('[data-upsell-compare]');
    const button = card.querySelector('[data-upsell-add]');
    if (price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare;
      compare.hidden = !option.dataset.compare;
    }
    if (button) {
      button.dataset.variantId = select.value;
      button.dataset.quantity = option.dataset.quantity;
    }
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const previous = event.target.closest('[data-upsell-previous]');
    const next = event.target.closest('[data-upsell-next]');
    if (previous || next) {
      this.#page += next ? 1 : -1;
      this.#paginate();
      if ((next || previous).disabled) {
        this.querySelector(next ? '[data-upsell-previous]' : '[data-upsell-next]')?.focus();
      }
      return;
    }
    const button = event.target.closest('[data-upsell-add]');
    if (button instanceof HTMLButtonElement) this.#addProduct(button);
  };

  async #addProduct(button) {
    if (this.#adding || button.disabled) return;
    this.#adding = true;
    const restoreFocus = document.activeElement === button;
    const status = this.querySelector('[data-upsell-status]');
    if (status) status.textContent = '';
    const buttons = this.querySelectorAll('[data-upsell-add]');
    buttons.forEach((item) => { item.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    const variantId = button.dataset.variantId;
    const quantity = Number(button.dataset.quantity) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId))].slice(0, 5);
    const drawer = this.closest('theme-drawer');
    let addedResponse;

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorMessage);
      addedResponse = result;
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.addedMessage);
      const ajaxCart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { sections: result.sections, items: ajaxCart.items, source: 'cart-upsell', didError: false },
      });
      if (status) status.textContent = this.dataset.addedMessage;
    } catch (error) {
      if (addedResponse) {
        const sectionHtml = addedResponse.sections?.['cart-drawer-section'];
        const section = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
        const count = Number(section.querySelector('.theme-drawer__badge')?.textContent.trim());
        deferred.resolve({ detail: {
          sections: addedResponse.sections,
          itemCount: count || Number(this.dataset.cartCount) + quantity,
          source: 'cart-upsell',
          didError: false,
        } });
        if (status) status.textContent = this.dataset.addedMessage;
      } else {
        deferred.reject(error);
        const message = error instanceof Error && error.message !== 'Failed to fetch'
          ? error.message : this.dataset.errorMessage;
        if (status) status.textContent = message;
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      }
    } finally {
      this.#adding = false;
      button.removeAttribute('aria-busy');
      if (!addedResponse) {
        buttons.forEach((item) => { item.disabled = false; });
        if (restoreFocus && button.isConnected) button.focus();
      }
      if (addedResponse && restoreFocus) {
        requestAnimationFrame(() => {
          if (!button.isConnected || button.disabled) drawer?.querySelector('.theme-drawer__close-button')?.focus();
        });
      }
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
